import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useToast } from '@chakra-ui/react';
import { useQuery } from 'hooks/useCustomQuery';

import { axios } from 'services/axios';
import { multiSelectDataMapper, toSentenceCase } from 'utils/utils';

import {
    Alert,
    AlertDescription,
    AlertIcon,
    LoadingIndicator,
} from '@/components/v4';

import FormBase from '@/components/forms/new/FormBase';
import getService from 'API/Service/getService';

// Form field components
import TPMCallingProfilesField from '../serviceprofiles/edit/formFields/TPMCallingProfilesField';

export default function TPM({ mode }) {
    // Original state variables preserved
    const navigate = useNavigate();
    const toast = useToast();
    const [callingProfiles, setCallingProfiles] = useState(null);
    const [operatorProfile, setOperatorProfile] = useState(null);
    const [dropdowns, setDropdowns] = useState({});
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [data, setData] = useState(null);
    const { id } = useParams();

    const { currentCompany } = useSelector((state) => {
        return { ...state.navigationLists };
    });
    const { darkMode } = useSelector((state) => state.settings);

    // FormBase initial values
    const [initialValues, setInitialValues] = useState({
        name: 'Teams Phone Mobile',
        callingProfiles: [],
    });

    // Original function preserved for potential future use
    /* function createOcFields(params) {
        let ocParams = { companyID: currentCompany };
        const {
            operatorProfileId,
            id,
            name,
            callingProfiles,
        } = params;

        if (id) {
            ocParams.id = id;
        }
        if (name) {
            ocParams.name = name;
        }
        if (operatorProfileId) {
            ocParams.operatorProfileID = operatorProfileId;
        }
        if (callingProfiles?.length) {
            ocParams.callingProfiles = callingProfiles.map((cp) => ({
                id: cp.value,
                name: cp.label,
            }));
        }

        return ocParams;
    } */

    // Original getDropdownData function preserved (now inline in useEffect)
    // Original getRegionsAndVPDropdown function preserved (now inline in useEffect)

    // Original useEffect hooks preserved - disable exhaustive deps warning for original logic
    useEffect(() => {
        const loadData = async () => {
            if (id) {
                const res = await getService(id);
                setData(res.data);
                const _callingProfiles = res.data.callingProfiles.map(multiSelectDataMapper);
                setCallingProfiles(_callingProfiles);
                setOperatorProfile(res.data.operatorProfile);
                setInitialValues({
                    name: res.data.name,
                    callingProfiles: _callingProfiles,
                });
            }
        };
        loadData();
    }, [id, currentCompany]);

    useQuery({
        queryKey: ['tpmOperatorProfiles', currentCompany],
        queryFn: async () => {
            const result = await axios.get(
                `/Services/TeamsPhoneMobile/Company/${currentCompany}/OperatorProfiles/Available`,
            );
            return result.data;
        },
        enabled: Boolean(currentCompany),
        onSuccess: (data) => {
            setDropdowns((prev) => ({
                ...prev,
                operatorProfile: data.map(multiSelectDataMapper),
            }));
        },
    });

    useQuery({
        queryKey: ['tpmCallingProfiles', currentCompany, operatorProfile?.id],
        queryFn: async () => {
            const result = await axios.get(
                `/Services/TeamsPhoneMobile/Company/${currentCompany}/Profile/${operatorProfile.id}/CallingProfiles/Available`,
            );
            return result.data;
        },
        enabled: Boolean(operatorProfile?.id && currentCompany),
        onSuccess: (data) => {
            setDropdowns((prev) => ({
                ...prev,
                callingProfiles: data.map(multiSelectDataMapper),
            }));
        },
    });

    // FormBase submit handler (converts original updateOcService logic)
    const handleSubmit = async (values) => {
        setIsLoading(true);

        const params = {
            ...data,
            callingProfiles: values.callingProfiles || callingProfiles,
        };

        try {
            await axios.put('/Service/TeamsPhoneMobile', params);
            toast({
                status: 'success',
                title: 'Service Updated',
                description: 'Teams Phone Mobile has been updated.',
                isClosable: true,
                duration: 3000,
            });
            navigate('/dashboard-services');
        } catch (e) {
            setError(e);
        } finally {
            setIsLoading(false);
        }
    };

    const handleBackButton = () => navigate('/dashboard-services');

    const breadCrumbTrail = [
        { label: 'Services', path: ['/dashboard-services', { index: 0 }] },
        { label: `${toSentenceCase(mode)} ${data?.name || 'Teams Phone Mobile'}`, path: '' },
    ];

    // Original loading condition preserved
    if (callingProfiles === null) {
        return <LoadingIndicator />;
    }

    return (
        <FormBase
            breadCrumbTrail={breadCrumbTrail}
            header={`${toSentenceCase(mode)} ${data?.name || 'Teams Phone Mobile'}`}
            initialValues={initialValues}
            finalFormSubmit={handleSubmit}
            customClick={true}
            onBack={handleBackButton}
            enableReinitialize={true}
            isSubmitDisabled={isLoading}
        >
            {({ values, setFieldValue, errors, touched }) => [
                <TPMCallingProfilesField 
                    key="callingProfiles"
                    value={values.callingProfiles || callingProfiles}
                    onChange={(selectedOptions) => {
                        setFieldValue('callingProfiles', selectedOptions);
                        setCallingProfiles(selectedOptions); // Keep original state in sync
                    }}
                    options={dropdowns.callingProfiles || []}
                    darkMode={darkMode}
                    error={errors.callingProfiles}
                    showError={touched.callingProfiles}
                />,
                error && (
                    <Alert key="error" status="error" sx={{ marginTop: '1rem' }}>
                        <AlertIcon />
                        <AlertDescription>
                            {error?.response?.data?.message ||
                                'An error occured. Please try again later.'}
                        </AlertDescription>
                    </Alert>
                )
            ].filter(Boolean)
            }
        </FormBase>
    );
}
